import { StrictMode, useCallback, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Step1 } from '@/Step1';
import { Step2 } from '@/Step2';
import { Success } from '@/Success';
import { ErrorView } from '@/ErrorView';
import type { Submission } from '@/types';
import '@/globals.css';

const MOUNT_ID = 'mocy-revocation-root';

type Step = 'input' | 'confirm' | 'success' | 'error';

type State = {
	step: Step;
	submission: Submission | null;
	confirmedAt: string | null;
};

const initialState: State = {
	step: 'input',
	submission: null,
	confirmedAt: null,
};

const App = () => {
	const [state, setState] = useState<State>(initialState);

	const goToConfirm = useCallback((submission: Submission) => {
		setState({ step: 'confirm', submission, confirmedAt: null });
	}, []);

	const goToSuccess = useCallback((confirmedAt: string | null) => {
		setState((prev) => ({
			step: 'success',
			submission: prev.submission,
			confirmedAt: confirmedAt || new Date().toISOString(),
		}));
	}, []);

	const goToError = useCallback(() => {
		setState((prev) => ({ ...prev, step: 'error' }));
	}, []);

	const reset = useCallback(() => {
		setState(initialState);
	}, []);

	const restartWithSubmission = useCallback((submission: Submission) => {
		setState({ step: 'confirm', submission, confirmedAt: null });
	}, []);

	switch (state.step) {
		case 'confirm':
			if (!state.submission) return <Step1 onNext={goToConfirm} onError={goToError} />;
			return <Step2 submission={state.submission} onSuccess={goToSuccess} onError={goToError} onBack={reset} />;
		case 'success':
			if (!state.submission) return <ErrorView onReset={reset} />;
			return (
				<Success
					confirmedAt={state.confirmedAt}
					previousSubmission={state.submission}
					onRestart={restartWithSubmission}
					onFinished={reset}
					onError={goToError}
				/>
			);
		case 'error':
			return <ErrorView onReset={reset} />;
		case 'input':
		default:
			return <Step1 onNext={goToConfirm} onError={goToError} />;
	}
};

const mount = () => {
	const container = document.getElementById(MOUNT_ID);
	if (!container) return;
	const root = createRoot(container);
	root.render(
		<StrictMode>
			<App />
		</StrictMode>
	);
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', mount, { once: true });
} else {
	mount();
}
